import { defineStore } from 'pinia';
import { AuthenticationApiClient, IToken, LtiIdentity, ILtiIdentity } from '@cyber-range/cyber-range-api-authentication-client';
import Config from '@/config';
import * as JsonWebToken from 'jsonwebtoken';
import { useApiClientStore } from './apiClientStore';

export const useAuthenticationStore = defineStore('authentication', {
    state: () => ({
        identityProvider: "",
        userId: '',
        token: '',
        displayName: '',
        tokenExpirationTimeInMs: 0,
        resources: [],
        ltiIdentity: new LtiIdentity(),
        ltiToken: ''
    }),
    getters: {
        isLogin(): boolean {
            return this.token ? new Date().getTime() < this.tokenExpirationTimeInMs : false;
        }
    },
    actions: {
        resetIdentityProvider(value: string) {
            this.identityProvider = value;
            if (value) {
                localStorage.setItem('identityProvider', value);
            }
            else {
                localStorage.removeItem('identityProvider');
            }
        },
        setToken(token: IToken) {
            const tokenString = token ? token.value : undefined;

            this.token = tokenString;

            if (tokenString) {
                const jwt = JsonWebToken.decode(tokenString);
                this.tokenExpirationTimeInMs = (jwt['exp'] - 30) * 1000; // 30 seconds

                this.identityProvider = jwt['provider'];
                this.userId = jwt['sub'];
                this.displayName = jwt['displayName'];
                localStorage.setItem('identityProvider', this.identityProvider);
                sessionStorage.setItem('token', this.token);
            } else {
                this.tokenExpirationTimeInMs = 0;
            }
        },
        setLtiIdentity(value: ILtiIdentity) {
            this.ltiIdentity = value;
        },
        setLtiToken(token: string) {
            this.ltiToken = token;
        },
        async login(request: { token: string, provider: string }): Promise<boolean> {
            const client = new AuthenticationApiClient(Config.AUTHENTICATION_API_BASE_URL);

            try {
                let token = await client.authenticate(request.provider, request.token);
                this.setToken(token);
            } catch {
                return false;
            }

            return true;
        },
        async tryLogin(): Promise<boolean> {
            const token = this.token;
            if (token) {
                return this.login({ token, provider: 'vcr' });
            } else {
                return false;
            }
        },
        async validateToken(token: string): Promise<boolean> {
            const client = new AuthenticationApiClient(Config.AUTHENTICATION_API_BASE_URL);

            try {
                return await client.validate(token);
            } catch {
                return false;
            }
        },
        async getSsoToken(): Promise<string> {
            const apiClientStore = useApiClientStore();
            return apiClientStore.authenticationApiClient.sso();
        },
        async detectSystemClockDrift(): Promise<boolean> {
            const systemTimeEpochMs = new Date().getTime();
            const apiClientStore = useApiClientStore();
            const serverTimeEpochMs = new Date(await apiClientStore.backgroundAuthenticationApiClient.getCurrentTimestamp()).getTime();
            if (Math.abs(systemTimeEpochMs - serverTimeEpochMs) > Config.SYSTEM_CLOCK_DRIFT_THRESHHOLD_IN_MS) {
                return true;
            }
            return false;
        }
    }
});